import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { MailrailSingleSendEmailCampaign } from '@feathr/blackbox';
import { EEmailABTestStatus, EMAIL_AB_TEST_VARIATIONS } from '@feathr/blackbox';
import MailrailEmailDetails from '@feathr/extender/components/MailrailEmailDetails';
import { getEmailABTest, getEmailABTestVariationMailing } from '@feathr/extender/utils/emailABTest';

import ABTestPerformanceChart from './ABTestPerformanceChart';
import type { IABTestReportStats } from './ABTestReport.types';
import { getABTestTimelineProgress, getABTestVersionPillStatus } from './ABTestReport.utils';
import ABTestSummary from './ABTestSummary';
import ABTestTimeline from './ABTestTimeline';
import ABTestVersionChart from './ABTestVersionChart';
import ABTestVersionPills from './ABTestVersionPills';

import * as styles from './ABTestReport.css';

interface IProps {
  campaign: MailrailSingleSendEmailCampaign;
  stats?: IABTestReportStats;
}

function ABTestReport({ campaign, stats }: Readonly<IProps>): JSX.Element {
  const abTest = getEmailABTest(campaign);
  const progress = getABTestTimelineProgress(
    abTest.test_started_at ?? campaign.get('date_start'),
    campaign.get('winner_send_at'),
  );

  return (
    <div>
      {abTest.status === EEmailABTestStatus.Testing && <ABTestTimeline progress={progress} />}
      {EMAIL_AB_TEST_VARIATIONS.map((variation) => {
        const mailing = getEmailABTestVariationMailing(campaign, variation);
        if (variation !== 'a' && !mailing) {
          return null;
        }
        return (
          <MailrailEmailDetails
            campaign={campaign}
            key={variation}
            mailing={mailing}
            pills={
              <ABTestVersionPills
                status={getABTestVersionPillStatus(variation, abTest)}
                variation={variation}
              />
            }
          />
        );
      })}
      <ABTestSummary abTest={abTest} summary={stats?.summary} />
      <ABTestPerformanceChart performance={stats?.performance} />
      <div className={styles.versionCharts}>
        {EMAIL_AB_TEST_VARIATIONS.map((variation) => (
          <div key={variation}>
            <ABTestVersionChart activity={stats?.activity[variation]} variation={variation} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default observer(ABTestReport);
